// iter65 — Voice-page WebRTC two-way audio.
// Mounted once in Layout. Listens for incoming pa:voice_invite via window 'app:ws_event'
// and outgoing 'app:start_voice_call' events. Manages a single RTCPeerConnection at a time.
//
// Signaling kinds (relayed by /api/audio/voice-signal):
//   invite  → sent by POST /api/audio/page (mode=voice). Recipient gets pa:voice_invite.
//   accept  → recipient accepted. Sender then creates+sends offer.
//   decline → recipient rejected. Sender cleans up.
//   offer   → SDP offer from sender.
//   answer  → SDP answer from recipient.
//   ice     → ICE candidates (both directions).
//   hangup  → either side ends the call.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Mic, MicOff, PhoneOff, Phone, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const RTC_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};
const INVITE_TIMEOUT_MS = 25_000;
const DEFAULT_MAX_SECONDS = 60;

function api() {
  const token = localStorage.getItem('token');
  return axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} });
}

function playRingChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    const ctx = new Ctx();
    let stopped = false;
    const tick = (offset) => {
      if (stopped) return;
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.frequency.value = 880;
      osc.type = 'sine';
      const t = ctx.currentTime + offset;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.4, t + 0.05);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
      osc.connect(g).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.5);
    };
    let beat = 0;
    const id = setInterval(() => {
      if (stopped) { clearInterval(id); ctx.close().catch(() => {}); return; }
      tick(0); tick(0.6);
      beat += 1;
      if (beat > 30) { stopped = true; clearInterval(id); ctx.close().catch(() => {}); }
    }, 1500);
    tick(0); tick(0.6);
    return () => { stopped = true; clearInterval(id); try { ctx.close(); } catch (_) {} };
  } catch (_) { return null; }
}

export default function VoiceCallManager() {
  // state machine: idle | incoming | outgoing | connecting | active
  const [state, setState] = useState('idle');
  const [peer, setPeer] = useState(null);          // {id, name, priority}
  const [muted, setMuted] = useState(false);
  const [callStart, setCallStart] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [maxSeconds, setMaxSeconds] = useState(DEFAULT_MAX_SECONDS);

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const iceQueueRef = useRef([]);
  const inviteTimerRef = useRef(null);
  const ringStopRef = useRef(null);
  const stateRef = useRef(state);     // for use inside async handlers
  const peerRef = useRef(peer);

  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => { peerRef.current = peer; }, [peer]);

  // Load max call seconds from admin config
  useEffect(() => {
    api().get('/admin/audio-config')
      .then(r => { if (r.data?.max_voice_seconds) setMaxSeconds(r.data.max_voice_seconds); })
      .catch(() => {});
  }, []);

  // Call timer
  useEffect(() => {
    if (state !== 'active' || !callStart) return undefined;
    const id = setInterval(() => {
      const e = Math.floor((Date.now() - callStart) / 1000);
      setElapsed(e);
      if (e >= maxSeconds) {
        toast.info(`Call ended (max ${maxSeconds}s reached)`);
        hangup();
      }
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, callStart, maxSeconds]);

  const cleanup = useCallback((reason) => {
    if (pcRef.current) { try { pcRef.current.close(); } catch (_) {} pcRef.current = null; }
    if (localStreamRef.current) { localStreamRef.current.getTracks().forEach(t => t.stop()); localStreamRef.current = null; }
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
    if (inviteTimerRef.current) { clearTimeout(inviteTimerRef.current); inviteTimerRef.current = null; }
    if (ringStopRef.current) { ringStopRef.current(); ringStopRef.current = null; }
    iceQueueRef.current = [];
    setPeer(null);
    setMuted(false);
    setCallStart(null);
    setElapsed(0);
    setState('idle');
    if (reason) toast.info(reason);
  }, []);

  const ensureMicAndPc = useCallback(async () => {
    if (pcRef.current) return pcRef.current;
    try {
      localStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    } catch (e) {
      toast.error('Microphone permission denied');
      cleanup();
      throw e;
    }
    const pc = new RTCPeerConnection(RTC_CONFIG);
    pcRef.current = pc;
    localStreamRef.current.getTracks().forEach(t => pc.addTrack(t, localStreamRef.current));
    pc.onicecandidate = (ev) => {
      const p = peerRef.current;
      if (ev.candidate && p) {
        api().post('/audio/voice-signal', { to_user_id: p.id, kind: 'ice', payload: { candidate: ev.candidate.toJSON ? ev.candidate.toJSON() : ev.candidate } })
          .catch(() => {});
      }
    };
    pc.ontrack = (ev) => {
      if (remoteAudioRef.current && ev.streams && ev.streams[0]) {
        remoteAudioRef.current.srcObject = ev.streams[0];
        remoteAudioRef.current.play().catch(() => {});
      }
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        setState('active');
        setCallStart(Date.now());
        if (ringStopRef.current) { ringStopRef.current(); ringStopRef.current = null; }
      } else if (['failed', 'closed'].includes(pc.connectionState)) {
        cleanup('Call ended');
      } else if (pc.connectionState === 'disconnected') {
        // Sometimes recoverable — give it 5s
        setTimeout(() => {
          if (pcRef.current && pcRef.current.connectionState === 'disconnected') cleanup('Call dropped');
        }, 5000);
      }
    };
    return pc;
  }, [cleanup]);

  const hangup = useCallback(async () => {
    const p = peerRef.current;
    if (p) {
      await api().post('/audio/voice-signal', { to_user_id: p.id, kind: 'hangup', payload: {} }).catch(() => {});
    }
    cleanup();
  }, [cleanup]);

  // ====== Outgoing flow ======
  // Triggered by PersonalAudio after it sent a mode=voice page.
  // The recipient already has a pa:voice_invite event waiting.
  // We DEFER getUserMedia until the recipient accepts (better UX: caller
  // sees "Calling…" before any mic prompt; mic only prompted right when
  // we actually need to send the offer SDP).
  const startOutgoing = useCallback(async ({ userId, userName, priority = 'normal' }) => {
    if (stateRef.current !== 'idle') {
      toast.error('Already on a call');
      return;
    }
    setPeer({ id: userId, name: userName, priority });
    setState('outgoing');
    inviteTimerRef.current = setTimeout(() => {
      if (stateRef.current === 'outgoing') {
        cleanup('No answer');
      }
    }, INVITE_TIMEOUT_MS);
  }, [cleanup]);

  // ====== WS event handlers ======
  const onIncomingInvite = useCallback(async (d) => {
    if (stateRef.current !== 'idle') {
      // Already busy → auto-decline
      try {
        await api().post('/audio/voice-signal', { to_user_id: d.from_user_id, kind: 'decline', payload: { reason: 'busy' } });
      } catch (_) { /* ignore */ }
      return;
    }
    setPeer({ id: d.from_user_id, name: d.from_name || 'Caller', priority: d.priority || 'normal' });
    setState('incoming');
    ringStopRef.current = playRingChime();
    inviteTimerRef.current = setTimeout(() => {
      if (stateRef.current === 'incoming') {
        api().post('/audio/voice-signal', { to_user_id: d.from_user_id, kind: 'decline', payload: { reason: 'timeout' } }).catch(() => {});
        cleanup('Missed call');
      }
    }, INVITE_TIMEOUT_MS);
  }, [cleanup]);

  const accept = useCallback(async () => {
    if (stateRef.current !== 'incoming') return;
    if (ringStopRef.current) { ringStopRef.current(); ringStopRef.current = null; }
    if (inviteTimerRef.current) { clearTimeout(inviteTimerRef.current); inviteTimerRef.current = null; }
    const p = peerRef.current;
    // iter65 fix: notify caller FIRST so they aren't left hanging if our mic fails
    if (p) {
      api().post('/audio/voice-signal', { to_user_id: p.id, kind: 'accept', payload: {} }).catch(() => {});
    }
    setState('connecting');
    try {
      await ensureMicAndPc();
    } catch (_) {
      // Mic failed → tell the caller to hang up instead of leaving them stuck
      if (p) {
        api().post('/audio/voice-signal', { to_user_id: p.id, kind: 'hangup', payload: { reason: 'mic_unavailable' } }).catch(() => {});
      }
    }
  }, [ensureMicAndPc]);

  const decline = useCallback(async () => {
    const p = peerRef.current;
    if (p) {
      await api().post('/audio/voice-signal', { to_user_id: p.id, kind: 'decline', payload: {} }).catch(() => {});
    }
    cleanup();
  }, [cleanup]);

  const onAccept = useCallback(async (d) => {
    if (stateRef.current !== 'outgoing' || !peerRef.current || d.from_user_id !== peerRef.current.id) return;
    if (inviteTimerRef.current) { clearTimeout(inviteTimerRef.current); inviteTimerRef.current = null; }
    setState('connecting');
    let pc;
    try {
      pc = pcRef.current || await ensureMicAndPc();
    } catch (_) {
      // Caller mic failed → hang up the now-accepted call gracefully
      const p = peerRef.current;
      if (p) {
        api().post('/audio/voice-signal', { to_user_id: p.id, kind: 'hangup', payload: { reason: 'mic_unavailable' } }).catch(() => {});
      }
      return;
    }
    const offer = await pc.createOffer({ offerToReceiveAudio: true });
    await pc.setLocalDescription(offer);
    const p = peerRef.current;
    await api().post('/audio/voice-signal', { to_user_id: p.id, kind: 'offer', payload: { sdp: offer } }).catch(() => {});
  }, [ensureMicAndPc]);

  const onDecline = useCallback(async (d) => {
    if (!peerRef.current || d.from_user_id !== peerRef.current.id) return;
    cleanup(`${peerRef.current.name} declined`);
  }, [cleanup]);

  const onOffer = useCallback(async (d) => {
    if (!peerRef.current || d.from_user_id !== peerRef.current.id) return;
    const pc = pcRef.current || await ensureMicAndPc();
    await pc.setRemoteDescription(new RTCSessionDescription(d.payload.sdp));
    while (iceQueueRef.current.length > 0) {
      try { await pc.addIceCandidate(iceQueueRef.current.shift()); } catch (_) {}
    }
    const ans = await pc.createAnswer();
    await pc.setLocalDescription(ans);
    await api().post('/audio/voice-signal', { to_user_id: peerRef.current.id, kind: 'answer', payload: { sdp: ans } }).catch(() => {});
  }, [ensureMicAndPc]);

  const onAnswer = useCallback(async (d) => {
    if (!pcRef.current || !peerRef.current || d.from_user_id !== peerRef.current.id) return;
    await pcRef.current.setRemoteDescription(new RTCSessionDescription(d.payload.sdp));
    while (iceQueueRef.current.length > 0) {
      try { await pcRef.current.addIceCandidate(iceQueueRef.current.shift()); } catch (_) {}
    }
  }, []);

  const onIce = useCallback(async (d) => {
    if (!peerRef.current || d.from_user_id !== peerRef.current.id) return;
    const cand = d.payload?.candidate;
    if (!cand) return;
    if (pcRef.current && pcRef.current.remoteDescription) {
      try { await pcRef.current.addIceCandidate(cand); } catch (_) {}
    } else {
      iceQueueRef.current.push(cand);
    }
  }, []);

  const onHangup = useCallback(() => { cleanup('Call ended'); }, [cleanup]);

  // Listen for incoming WS events + outgoing call trigger
  useEffect(() => {
    const wsHandler = (ev) => {
      const msg = ev.detail;
      if (!msg?.type) return;
      const d = msg.data || {};
      switch (msg.type) {
        case 'pa:voice_invite': onIncomingInvite(d); break;
        case 'pa:voice_accept': onAccept(d); break;
        case 'pa:voice_decline': onDecline(d); break;
        case 'pa:voice_offer': onOffer(d); break;
        case 'pa:voice_answer': onAnswer(d); break;
        case 'pa:voice_ice': onIce(d); break;
        case 'pa:voice_hangup': onHangup(); break;
        default: break;
      }
    };
    const startHandler = (ev) => startOutgoing(ev.detail || {});
    window.addEventListener('app:ws_event', wsHandler);
    window.addEventListener('app:start_voice_call', startHandler);
    return () => {
      window.removeEventListener('app:ws_event', wsHandler);
      window.removeEventListener('app:start_voice_call', startHandler);
    };
  }, [onIncomingInvite, onAccept, onDecline, onOffer, onAnswer, onIce, onHangup, startOutgoing]);

  const toggleMute = () => {
    if (!localStreamRef.current) return;
    const next = !muted;
    localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = !next; });
    setMuted(next);
  };

  if (state === 'idle') {
    // hidden audio sink ready for next call
    return <audio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} data-testid="voice-remote-audio" />;
  }

  // Visible UI overlay
  const isIncoming = state === 'incoming';
  const isOutgoing = state === 'outgoing';
  const isConnecting = state === 'connecting';
  const isActive = state === 'active';

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <audio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} data-testid="voice-remote-audio" />
      <div
        className={`fixed top-2 right-2 z-[60] w-[320px] shadow-2xl rounded-xl border-2 p-4 transition-all
          ${isIncoming ? 'bg-blue-600 text-white border-blue-300 animate-pulse' :
            isActive ? 'bg-emerald-600 text-white border-emerald-300' :
            'bg-slate-800 text-white border-slate-500'}`}
        data-testid="voice-call-overlay"
      >
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-xs uppercase tracking-wide opacity-80">
              {isIncoming && (peer?.priority === 'emergency' ? '⚠ EMERGENCY VOICE PAGE' : 'Incoming voice call')}
              {isOutgoing && 'Calling…'}
              {isConnecting && 'Connecting…'}
              {isActive && 'In Call'}
            </div>
            <div className="text-base font-semibold truncate" data-testid="voice-peer-name">{peer?.name}</div>
          </div>
          {!isIncoming && !isActive && (
            <button onClick={hangup} className="text-white/80 hover:text-white" data-testid="voice-cancel-btn">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {isActive && (
          <div className="text-sm font-mono opacity-80 mb-2" data-testid="voice-elapsed">
            {fmt(elapsed)} / {fmt(maxSeconds)}
          </div>
        )}

        <div className="flex gap-2">
          {isIncoming && (
            <>
              <Button
                onClick={accept}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="voice-accept-btn"
              >
                <Phone className="h-4 w-4 mr-2" /> Accept
              </Button>
              <Button
                onClick={decline}
                variant="outline"
                className="flex-1 bg-white/10 border-white/30 text-white hover:bg-white/20"
                data-testid="voice-decline-btn"
              >
                <PhoneOff className="h-4 w-4 mr-2" /> Decline
              </Button>
            </>
          )}
          {(isOutgoing || isConnecting) && (
            <Button
              onClick={hangup}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              data-testid="voice-hangup-btn"
            >
              <PhoneOff className="h-4 w-4 mr-2" /> Cancel
            </Button>
          )}
          {isActive && (
            <>
              <Button
                onClick={toggleMute}
                variant="outline"
                className="flex-1 bg-white/10 border-white/30 text-white hover:bg-white/20"
                data-testid="voice-mute-btn"
              >
                {muted
                  ? <><MicOff className="h-4 w-4 mr-2" /> Unmute</>
                  : <><Mic className="h-4 w-4 mr-2" /> Mute</>}
              </Button>
              <Button
                onClick={hangup}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                data-testid="voice-hangup-btn"
              >
                <PhoneOff className="h-4 w-4 mr-2" /> Hang Up
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
